/* tslint:disable:max-line-length */
import { AccordionItem, Heading, Link, TextLink } from '@mm/components';
import classNames from 'classnames';
import { partial } from 'lodash';
import React from 'react';
import useRouter from 'use-react-router';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../common/buttons/PrimaryButton/PrimaryButton';
import { trackWebAppGAEvent } from '../../../common/helpers/plugins/website-google-analytics';
import { SectionFooter } from '../../../dashboard/pages/SignUp/SignUp/SectionFooter/SectionFooter';
import { Logo } from '../../../layout/BotBuilderSidebar/Logo/Logo';
import { Routes } from '../../../routes/top-level-routes';
import { ReactComponent as ChatImage } from './001-chat.svg';
import { ReactComponent as ButtonImage } from './002-button.svg';
import { ReactComponent as PhoneImage } from './003-smartphone.svg';
import { ReactComponent as EngagementImage } from './004-engagement.svg';
import facebookBadgeOne from './badge-1.png';
import facebookBadgeTwo from './badge-2.png';
import './FreeLeadGeneratorLanding.pcss';
import HeroImage from './hero-landing-compresed.png';

type Props = {
  className?: string;
};

const business = {
  title: 'Automate your Messenger Responses',
  subTitle:
    'Manage high message volume & improve your response time with an automatic Facebook Page Welcomer.',
  bodyAttention: "Don't keep customers waiting",
  body: 'Add free, automated responses to your Facebook Page and Posts to keep users engaged and capture critical contact info so you can follow up ASAP.',
  cta: 'Set up my Facebook Welcomer',
  toolName: 'Facebook Page Welcomer',
};

const defaults = {
  title: 'Free Facebook Lead Generator',
  subTitle: 'Capture Emails and Phone Numbers from Facebook — Automatically!',
  bodyAttention: "Don't let hot leads on Facebook fall through the cracks.",
  body:
    'Add a free, automated answering service to your Facebook Page and\n' +
    '              Posts to capture critical contact info so you can follow-up with\n' +
    '              prospects fast using the Free Facebook Lead Generator.',
  cta: 'Capture My Facebook Leads!',
  toolName: 'Facebook Lead Generator',
};

export const FreeLeadGeneratorLanding = ({ className }: Props) => {
  const router = useRouter();

  const isAutomatedResponseTool = router.location.pathname.includes(
    Routes.automatedResponseTool.path,
  );

  const path = isAutomatedResponseTool
    ? Routes.automatedResponseTool.path
    : Routes.freeLeadGenerator.path;

  const onStartBtnClick = (source: string) => {
    trackWebAppGAEvent('click', {
      category: isAutomatedResponseTool
        ? 'automated_response_tool'
        : 'free_lead_generator',
      label: `start_btn:${source}`,
    });

    router.history.push(`${path}/start`);
  };

  const content = isAutomatedResponseTool ? business : defaults;

  return (
    <div className={classNames(className, 'free-lead-generator-landing')}>
      <section className="free-lead-generator-landing__hero">
        <span className="free-lead-generator-landing__background" />
        <div className="free-lead-generator-landing__header">
          <Logo className="free-lead-generator-landing__header-logo" />

          <div className="free-lead-generator-landing__header-badges">
            <div className="free-lead-generator-landing__header-badge">
              <img src={facebookBadgeOne} alt="Facebook Approval Badge" />
            </div>
            <div className="free-lead-generator-landing__header-badge">
              <img src={facebookBadgeTwo} alt="Facebook Approval Badge" />
            </div>
          </div>

          <div className="free-lead-generator-landing__header-links">
            <Link
              className="free-lead-generator-landing__header-link"
              rel="noopener noreferrer"
              href="https://customers.ai/our-team/"
              isBlank={true}
            >
              About Us
            </Link>
            <Link
              className="free-lead-generator-landing__header-link"
              rel="noopener noreferrer"
              href="https://customers.ai/#contact"
              isBlank={true}
            >
              Contacts
            </Link>
            <PrimaryButton
              onClick={partial(onStartBtnClick, 'header-mobile')}
              className="free-lead-generator-landing__header-start-btn"
            >
              Get Started Free
            </PrimaryButton>
          </div>
        </div>
        <div className="free-lead-generator-landing__hero-content">
          <div className="free-lead-generator-landing__hero-copy">
            <div className="free-lead-generator-landing__hero-title">
              {content.title}
            </div>
            <span className="free-lead-generator-landing__hero-subtitle">
              {content.subTitle}
            </span>
            <PrimaryButton
              onClick={partial(onStartBtnClick, 'header-desktop')}
              color={ButtonColor.greenGradient}
              className="free-lead-generator-landing__start-btn free-lead-generator-landing__start-btn--desktop"
            >
              {content.cta}
            </PrimaryButton>

            <div className="free-lead-generator-landing__hero-copy-additional">
              <strong>{content.bodyAttention}</strong>
              <br />
              <br />
              {content.body}
            </div>
          </div>

          <img
            src={HeroImage}
            className="free-lead-generator-landing__hero-img"
            alt="Facebook Hero"
          />

          <PrimaryButton
            onClick={partial(onStartBtnClick, 'hero')}
            className="free-lead-generator-landing__start-btn"
          >
            {content.cta}
          </PrimaryButton>
        </div>
      </section>

      <section className="free-lead-generator-landing__steps">
        <Heading
          className="free-lead-generator-landing__steps-heading"
          size={2}
          text={`How the Free ${content.toolName} works: `}
        />
        <div className="free-lead-generator-landing__steps-container">
          <div className="free-lead-generator-landing__step">
            <div className="free-lead-generator-landing__step-img">
              <ButtonImage />
            </div>
            <div className="free-lead-generator-landing__step-description">
              Fans and prospects message your Facebook Page and comment on
              Posts.
            </div>
          </div>
          <div className="free-lead-generator-landing__step">
            <div className="free-lead-generator-landing__step-img">
              <PhoneImage />
            </div>
            <div className="free-lead-generator-landing__step-description">
              Customers.ai auto-replies in Messenger to collect emails and phone
              numbers.
            </div>
          </div>
          <div className="free-lead-generator-landing__step">
            <div className="free-lead-generator-landing__step-img">
              <ChatImage />
            </div>
            <div className="free-lead-generator-landing__step-description">
              Then we send you a daily email report containing all your new
              leads
            </div>
          </div>
          <div className="free-lead-generator-landing__step">
            <div className="free-lead-generator-landing__step-img">
              <EngagementImage />
            </div>
            <div className="free-lead-generator-landing__step-description">
              Use Customers.ai to re-engage your leads at any time!
            </div>
          </div>
        </div>
      </section>

      <section className="free-lead-generator-landing__faq">
        <span className="free-lead-generator-landing__background" />

        <Heading size={2}>
          {`Frequently Asked Questions about the Free ${content.toolName}`}
        </Heading>
        <div className="free-lead-generator-landing__faq-container">
          <AccordionItem
            closeOnOutsideClick={false}
            className="free-lead-generator-landing__faq-item"
            title="What are chatbots?"
            arrowPosition="left"
          >
            <TextLink className="free-lead-generator-landing__faq-content">
              Chatbots are basically computer programs that can have a
              conversation with a person. Most chatbots today aren’t trying to
              pose as a real person or pass the Turing test. Instead, they’re
              used to automate tasks on messaging platforms like Facebook
              Messenger, SMS text messaging, and native website chat.
              <br />
              <br />
              {`The Free ${content.toolName} is a simple chatbot that captures`}
              lead contact information from people who message your Facebook
              Page and comment on Facebook Posts.
              <br />
              <br />A few things you can do with a Customers.ai chatbot include
              lead capture forms, automating instant FAQs, booking appointments,
              downloading digital resources, and webinar registrations.
            </TextLink>
          </AccordionItem>

          <AccordionItem
            closeOnOutsideClick={false}
            className="free-lead-generator-landing__faq-item"
            title="What are OmniChat chatbots?"
            arrowPosition="left"
          >
            <TextLink className="free-lead-generator-landing__faq-content">
              Customers.ai’s patented OmniChat technology translates chatbots
              into many popular messaging apps.
              <br />
              <br />
              Basically, marketers write the conversation, lead form, or chatbot
              integration once.
              <br />
              <br />
              Then Customers.ai makes that campaign work in popular messaging
              apps — like Facebook Messenger, SMS, and native web chat —
              simultaneously.
              <br />
              <br />
              OmniChat chatbots explode the reach of chat marketing campaigns
              without any extra effort by the business owner or marketer
              creating the campaign. OmniChat streamlines chats across messaging
              platforms into a single unified management platform.
              <br />
              <br />
              And leads and customers have the flexibility to use the messaging
              app that they prefer. Win-win.
            </TextLink>
          </AccordionItem>

          <AccordionItem
            closeOnOutsideClick={false}
            className="free-lead-generator-landing__faq-item"
            title="What are chatbots used for?"
            arrowPosition="left"
          >
            <TextLink className="free-lead-generator-landing__faq-content">
              Here are just a few of the ways chatbots are helping businesses
              reach their goals faster:
              <br />
              <br />
              Marketing: Top-of-funnel lead generation, lead qualification,
              remarketing and engagement. Use marketing bots to run contests,
              send promos, provide coupons, increase webinar attendance, and
              more.
              {`The Free ${content.toolName} is a simple marketing
              chatbot for top-of-funnel lead capture on Facebook.`}
              <br />
              <br />
              Sales: Connect sales teams to high-value buyers and engage buyers
              at critical points in the buyer journey to increase conversions.
              Use sales bots to book demos and set up appointments, as well as
              re-engage abandoned cart shoppers and send strategic offers to
              increase conversions.
            </TextLink>
          </AccordionItem>

          <AccordionItem
            closeOnOutsideClick={false}
            className="free-lead-generator-landing__faq-item"
            title="How much does it cost?"
            arrowPosition="left"
          >
            <TextLink className="free-lead-generator-landing__faq-content">
              {`The Free ${content.toolName} is $0 and 0¢ each month`}. But only
              for an unlimited number of leads. Ending never. But seriously, all
              the features in the Free Facebook Lead Generator are free with no
              hidden fees.
            </TextLink>
          </AccordionItem>
        </div>
      </section>

      <section className="free-lead-generator-landing__lead">
        <Heading className="free-lead-generator-landing__lead-title" size={2}>
          {`Fire Up the Free ${content.toolName}!`}
        </Heading>
        <PrimaryButton
          onClick={partial(onStartBtnClick, 'lead-footer')}
          className="free-lead-generator-landing__start-btn"
        >
          {content.cta}
        </PrimaryButton>
      </section>

      <SectionFooter className="free-lead-generator__footer" />
    </div>
  );
};
